"use client";

import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";

/**
 * Кнопка голосового ввода через Web Speech API (браузерное распознавание, ru-RU).
 * Показывается только если браузер поддерживает распознавание (Chrome/Edge).
 * По окончании фразы отдаёт распознанный текст через onText (композер добавляет его).
 */
export function VoiceButton({ onText, size = 16 }: { onText: (t: string) => void; size?: number }) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef<{ stop: () => void } | null>(null);

  useEffect(() => {
    const SR = typeof window !== "undefined"
      ? ((window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }).SpeechRecognition
        || (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition)
      : undefined;
    setSupported(!!SR);
    return () => { try { recRef.current?.stop(); } catch { /* ignore */ } };
  }, []);

  function toggle() {
    const w = window as unknown as { SpeechRecognition?: new () => never; webkitSpeechRecognition?: new () => never };
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) { recRef.current?.stop(); return; }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rec: any = new (SR as unknown as { new (): any })();
    rec.lang = "ru-RU";
    rec.interimResults = false;
    rec.continuous = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onresult = (e: any) => {
      const t = Array.from(e.results as ArrayLike<{ 0: { transcript: string } }>)
        .map((r) => r[0].transcript).join(" ").trim();
      if (t) onText(t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  }

  if (!supported) return null;
  return (
    <button type="button" onClick={toggle} aria-label="Голосовой ввод"
      data-tooltip={listening ? "Идёт запись — нажмите, чтобы остановить" : "Голосовой ввод"}
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition"
      style={listening ? { background: "#ef4444", color: "#fff", boxShadow: "none" } : { color: "var(--textMuted)", boxShadow: "none" }}>
      <Mic size={size} className={listening ? "animate-pulse" : ""} />
    </button>
  );
}
